import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AppThemeProvider } from './context/ThemeContext';
import { BranchProvider } from './context/BranchContext';
import './theme/darkModeFix.css';

// Import Layout and Pages
import Layout from './components/Layout';
import Login from './pages/Login/Login';
import ProtectedRoute from './components/ProtectedRoute'; // Import once

/** Redirect /repairs/ssi-technical/:id → /ssi-technical/:id */
const SsiTechnicalLegacyRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/ssi-technical/${id}`} replace />;
};

// Import existing pages
import Dashboard from './pages/Dashboard/Dashboard';
import Products from './pages/Products/Products';
import ProductForm from './pages/Products/ProductForm';
import Customers from './pages/Customers/Customers';
import EditCustomer from './pages/Customers/EditCustomer';
import Invoices from './pages/Invoices/Invoices';
import InvoiceForm from './pages/Invoices/InvoiceForm';
import Sales from './pages/Sales/Sales';
import SalesForm from './pages/Sales/SalesForm';
import Rentals from './pages/Rentals/Rentals';
import RentalForm from './pages/Rentals/RentalForm';
import RentalView from './pages/Rentals/RentalView';
import RentalTrack from './pages/Rentals/RentalTrack';
import RentalCustomers from './pages/Rentals/RentalCustomers';
import RentalCustomerProfile from './pages/Rentals/RentalCustomerProfile';
import RentalPendingPayments from './pages/Rentals/RentalPendingPayments';
import RentalInvoices from './pages/Rentals/RentalInvoices';
import RentalInvoiceView from './pages/Rentals/RentalInvoiceView';
import RentalAgreement from './pages/Rentals/RentalAgreement';
import RentalDashboard from './pages/Rentals/RentalDashboard';
import RentalReport from './pages/Rentals/RentalReport';
import RentalOutside from './pages/Rentals/RentalOutside';
import RentalOutsideProfile from './pages/Rentals/RentalOutsideProfile';
import ReportsPage from './pages/Reports/ReportsPage';
import Settings from './pages/Settings/Settings';
import Quotations from './pages/Quotations/Quotations';
import QuotationForm from './pages/Quotations/QuotationForm';
import Users from './pages/Users/Users';
import UserForm from './pages/Users/UserForm';
import ViewSale from './pages/Sales/ViewSale';
import POS from './pages/POS/POS';
import POSSales from './pages/POS/POSSales';
import RepairList from './pages/Repairs/RepairList';
import RepairForm from './pages/Repairs/RepairForm';
import ServiceRequestForm from './pages/Repairs/ServiceRequestForm';
import ServiceRequestView from './pages/Repairs/ServiceRequestView';
import RepairWorkshop from './pages/Repairs/RepairWorkshop';
import PartRequests from './pages/Repairs/PartRequests';
import RepairInvoices from './pages/Repairs/RepairInvoices';
import SsiTechnicalPage from './pages/Repairs/SsiTechnicalPage';
import OutsourceTechniciansPage from './pages/Repairs/OutsourceTechniciansPage';
import InhouseTechniciansPage from './pages/Repairs/InhouseTechniciansPage';
import ReadyToCollect from './pages/Repairs/ReadyToCollect';
import RepairDone from './pages/Repairs/RepairDone';
import RepairQaPage from './pages/Repairs/RepairQaPage';
import RepairManagementReport from './pages/Repairs/RepairManagementReport';
import OutsourceRepairs from './pages/Repairs/OutsourceRepairs';
import RepairCustomers from './pages/Repairs/RepairCustomers';
import RepairCustomerProfile from './pages/Repairs/RepairCustomerProfile';
import Branches from './pages/Branches/Branches';
import BranchUsers from './pages/Branches/BranchUsers';
import BranchStaff from './pages/Branches/BranchStaff';
import LoginHistoryPage from './pages/Security/LoginHistory';

// ==================== IMPORT PRODUCT ISSUES PAGE ====================
import GRN from './pages/GRN/GRN';
import ProductIssues from './pages/ProductIssues/ProductIssues';
import OfficeIssue from './pages/OfficeIssue/OfficeIssue';

// Credit Management Pages
import CreditDashboard from './pages/Credit/CreditDashboard';
import CreditCustomers from './pages/Credit/CreditCustomers';
import OverdueAccounts from './pages/Credit/OverdueAccounts';
import PaymentCollection from './pages/Credit/PaymentCollection';

// Accounting Module
import AccountingRoutes from './pages/Accounting/AccountingRoutes';

// Public Route Component (for login page)
const PublicRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem('erp_auth_token');
  
  if (isAuthenticated) {
    // If already logged in, redirect to dashboard
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AppThemeProvider>
      <BranchProvider>
      <Router>
        <Routes>
          {/* Public Route - Login Page */}
          <Route path="/login" element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } />

          {/* Default route redirects to login */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* Protected Routes - All wrapped with Layout */}

          {/* Dashboard - Accessible to all authenticated users */}
          <Route path="/dashboard" element={
            <ProtectedRoute requiredRole="all">
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          } />

          {/* POS */}
          <Route path="/pos" element={
            <ProtectedRoute requiredPermission="pos">
              <Layout>
                <POS />
              </Layout>
            </ProtectedRoute>
          } />

          {/* POS Sales History */}
          <Route path="/pos/sales" element={
            <ProtectedRoute requiredPermission="pos_sales">
              <Layout>
                <POSSales />
              </Layout>
            </ProtectedRoute>
          } />

          {/* GRN - Goods Receipt */}
          <Route path="/grn" element={
            <ProtectedRoute requiredPermission="grn">
              <Layout>
                <GRN />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/office-issue" element={
            <ProtectedRoute requiredPermission="products">
              <Layout>
                <OfficeIssue />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Products - Admin only */}
          <Route path="/products" element={
            <ProtectedRoute requiredPermission="products">
              <Layout>
                <Products />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Product Form */}
          <Route path="/products/new" element={
            <ProtectedRoute requiredPermission="products" allowedRoles={['admin', 'super_admin']}>
              <Layout>
                <ProductForm />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/products/edit/:id" element={
            <ProtectedRoute requiredPermission="products" allowedRoles={['admin', 'super_admin']}>
              <Layout>
                <ProductForm />
              </Layout>
            </ProtectedRoute>
          } />

          {/* ==================== PRODUCT ISSUES ROUTE ==================== */}
          <Route path="/product-issues" element={
            <ProtectedRoute requiredPermission="products">
              <Layout>
                <ProductIssues />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Customers - Admin only */}
          <Route path="/customers" element={
            <ProtectedRoute requiredPermission="customers">
              <Layout>
                <Customers />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Edit Customer */}
          <Route path="/customers/edit/:id" element={
            <ProtectedRoute requiredPermission="customers">
              <Layout>
                <EditCustomer />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Invoices */}
          <Route path="/invoices" element={
            <ProtectedRoute requiredPermission="reports">
              <Layout>
                <Invoices />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/invoices/new" element={
            <ProtectedRoute requiredPermission="reports">
              <Layout>
                <InvoiceForm />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/invoices/edit/:id" element={
            <ProtectedRoute requiredPermission="reports">
              <Layout>
                <InvoiceForm />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/invoices/:id" element={
            <ProtectedRoute requiredPermission="reports">
              <Layout>
                <InvoiceForm />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Sales - Admin & Staff */}
          <Route path="/sales" element={
            <ProtectedRoute requiredPermission="reports">
              <Layout>
                <Sales />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/sales/new" element={
            <ProtectedRoute requiredPermission="reports">
              <Layout>
                <SalesForm />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/sales/edit/:id" element={
            <ProtectedRoute requiredPermission="reports">
              <Layout>
                <SalesForm />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/sales/view/:id" element={
            <ProtectedRoute requiredPermission="reports">
              <Layout>
                <ViewSale />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Quotations - Admin & Staff */}
          <Route path="/quotations" element={
            <ProtectedRoute requiredPermission="reports">
              <Layout>
                <Quotations />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/quotations/new" element={
            <ProtectedRoute requiredPermission="reports">
              <Layout>
                <QuotationForm />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/quotations/edit/:id" element={
            <ProtectedRoute requiredPermission="reports">
              <Layout>
                <QuotationForm />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Users - Admin only */}
          <Route path="/users" element={
            <ProtectedRoute requiredPermission="users">
              <Layout>
                <Users />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/users/new" element={
            <ProtectedRoute requiredPermission="users">
              <Layout>
                <UserForm />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/users/edit/:id" element={
            <ProtectedRoute requiredPermission="users">
              <Layout>
                <UserForm />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Rental */}
          <Route path="/rentals" element={
            <ProtectedRoute requiredPermission="rentals">
              <Layout><Rentals /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/rentals/dashboard" element={
            <ProtectedRoute requiredPermission="rentals">
              <Layout><RentalDashboard /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/rentals/report" element={
            <ProtectedRoute requiredPermission="rentals">
              <Layout><RentalReport /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/rentals/outside" element={
            <ProtectedRoute requiredPermission="rentals">
              <Layout><RentalOutside /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/rentals/outside/:serial" element={
            <ProtectedRoute requiredPermission="rentals">
              <Layout><RentalOutsideProfile /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/rentals/new" element={
            <ProtectedRoute requiredPermission="rentals">
              <Layout><RentalForm /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/rentals/track" element={
            <ProtectedRoute requiredPermission="rentals">
              <Layout><RentalTrack /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/rentals/pending" element={
            <ProtectedRoute requiredPermission="rentals">
              <Layout><RentalPendingPayments /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/rentals/invoices" element={
            <ProtectedRoute requiredPermission="rentals">
              <Layout><RentalInvoices /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/rentals/invoices/:invoiceId" element={
            <ProtectedRoute requiredPermission="rentals">
              <Layout><RentalInvoiceView /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/rentals/agreement" element={
            <ProtectedRoute requiredPermission="rentals">
              <Layout><RentalAgreement /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/rentals/customers" element={
            <ProtectedRoute requiredPermission="rentals">
              <Layout><RentalCustomers /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/rentals/customers/:customerId" element={
            <ProtectedRoute requiredPermission="rentals">
              <Layout><RentalCustomerProfile /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/rentals/:id" element={
            <ProtectedRoute requiredPermission="rentals">
              <Layout><RentalView /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute requiredPermission="reports">
              <Layout>
                <ReportsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute requiredPermission="settings">
              <Layout>
                <Settings />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Login History — Super Admin only */}
          <Route path="/login-history" element={
            <ProtectedRoute allowedRoles={['super_admin']} requiredPermission="login_history">
              <Layout>
                <LoginHistoryPage />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Credit Management Routes */}
          <Route path="/credit/dashboard" element={
            <ProtectedRoute requiredPermission="reports">
              <Layout>
                <CreditDashboard />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/credit/customers" element={
            <ProtectedRoute requiredPermission="reports">
              <Layout>
                <CreditCustomers />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/credit/overdue" element={
            <ProtectedRoute requiredPermission="reports">
              <Layout>
                <OverdueAccounts />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/credit/payments" element={
            <ProtectedRoute requiredPermission="reports">
              <Layout>
                <PaymentCollection />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Repair Management Routes */}
          <Route path="/repairs" element={
            <ProtectedRoute requiredPermission="repairs">
              <Layout><RepairList /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/repairs/new" element={
            <ProtectedRoute requiredPermission="repairs">
              <Layout><ServiceRequestForm /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/repairs/workshop" element={
            <ProtectedRoute requiredPermission="repairs">
              <Layout><RepairWorkshop /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/repairs/ready-to-collect" element={
            <ProtectedRoute requiredPermission="repairs">
              <Layout><ReadyToCollect /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/repairs/qa" element={
            <ProtectedRoute requiredPermission="repairs">
              <Layout><RepairQaPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/repairs/repair-done" element={
            <ProtectedRoute requiredPermission="repairs">
              <Layout><RepairDone /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/repairs/part-requests" element={
            <ProtectedRoute requiredPermission="repairs">
              <Layout><PartRequests /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/repairs/invoices" element={
            <ProtectedRoute requiredPermission="repairs">
              <Layout><RepairInvoices /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/repairs/outsource" element={
            <ProtectedRoute requiredPermission="repairs">
              <Layout><OutsourceRepairs /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/repairs/outsource/technician/:technicianName" element={
            <ProtectedRoute requiredPermission="repairs">
              <Layout><OutsourceRepairs /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/repairs/inhouse-technicians" element={
            <ProtectedRoute requiredPermission="repairs" allowedRoles={['admin', 'super_admin', 'technical_coordinator', 'admin_officer']}>
              <Layout><InhouseTechniciansPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/repairs/outsource-technicians" element={
            <ProtectedRoute requiredPermission="repairs" allowedRoles={['admin', 'super_admin']}>
              <Layout><OutsourceTechniciansPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/repairs/reports" element={
            <ProtectedRoute requiredPermission="repairs" allowedRoles={['admin', 'super_admin']}>
              <Layout><RepairManagementReport /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/repairs/customers" element={
            <ProtectedRoute requiredPermission="repairs">
              <Layout><RepairCustomers /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/repairs/customers/:customerId" element={
            <ProtectedRoute requiredPermission="repairs">
              <Layout><RepairCustomerProfile /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/ssi-technical" element={
            <ProtectedRoute requiredPermission="repairs">
              <Layout><SsiTechnicalPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/ssi-technical/:id" element={
            <ProtectedRoute requiredPermission="repairs">
              <Layout><SsiTechnicalPage /></Layout>
            </ProtectedRoute>
          } />
          {/* Old paths — avoid /repairs/:id catching "ssi-technical" as a repair id */}
          <Route path="/repairs/ssi-technical" element={<Navigate to="/ssi-technical" replace />} />
          <Route path="/repairs/ssi-technical/:id" element={<SsiTechnicalLegacyRedirect />} />
          <Route path="/repairs/edit/:id" element={
            <ProtectedRoute requiredPermission="repairs">
              <Layout><RepairForm /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/repairs/:id" element={
            <ProtectedRoute requiredPermission="repairs">
              <Layout><ServiceRequestView /></Layout>
            </ProtectedRoute>
          } />

          {/* Branches - Admin only */}
          <Route path="/branches" element={
            <ProtectedRoute requiredPermission="branches">
              <Layout><Branches /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/branches/users" element={
            <ProtectedRoute requiredPermission="branch_users">
              <Layout><BranchUsers /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/branch/staff" element={
            <ProtectedRoute requiredPermission="branch_staff">
              <Layout><BranchStaff /></Layout>
            </ProtectedRoute>
          } />

          {/* ==================== ACCOUNTING MODULE ROUTES ==================== */}
          <Route path="/accounting/*" element={
            <ProtectedRoute requiredPermission="accounting">
              <Layout>
                <AccountingRoutes />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Catch all - redirect to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
      </BranchProvider>
    </AppThemeProvider>
  );
}

export default App;